import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Scene from './components/Scene.tsx';
import Result from './components/Result.tsx';
import { scenes } from './data/scenes.ts';
import { MBTIScore } from './types/game.ts';
import { calculateMBTIType, getMBTIDescription } from './utils/mbti.ts';

function App() {
  const [isStarted, setIsStarted] = useState(false);
  const [currentScene, setCurrentScene] = useState(0);
  const [scores, setScores] = useState<MBTIScore>({
    E: 0, I: 0,
    S: 0, N: 0,
    T: 0, F: 0,
    J: 0, P: 0
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleStart = () => {
    setIsStarted(true);
  };

  const handleChoice = (choice: 'A' | 'B') => {
    if (scenes[currentScene].id === 'intro') {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentScene(1);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 750); // 第二个0.75秒用于新场景的淡入
      }, 750); // 第一个0.75秒用于当前场景的淡出
      return;
    }

    const currentSceneData = scenes[currentScene];
    if (!currentSceneData.choices) return;

    const selectedChoice = currentSceneData.choices[choice];
    setScores(prev => ({
      ...prev,
      [selectedChoice.score]: prev[selectedChoice.score] + 1
    }));

    setFeedback(selectedChoice.feedback);
    setShowFeedback(true);

    // 计算打字机效果需要的时间（每个字25ms）
    const typingDuration = selectedChoice.feedback.length * 25;
    // 等待打字机效果完成后2秒，再开始场景切换
    const waitAfterTyping = 2000;
    
    setTimeout(() => {
      setShowFeedback(false);
      if (currentScene < scenes.length - 1) {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentScene(prev => prev + 1);
          setTimeout(() => {
            setIsTransitioning(false);
          }, 750); // 第二个0.75秒用于新场景的淡入
        }, 750); // 第一个0.75秒用于当前场景的淡出
      } else {
        const type = calculateMBTIType(scores);
        setResult(type);
      }
    }, typingDuration + waitAfterTyping);
  };

  const handleLastSceneComplete = () => {
    // 等待2秒后显示结果
    setTimeout(() => {
      const type = calculateMBTIType(scores);
      setResult(type);
    }, 2000);
  };

  const handleRestart = () => {
    setIsStarted(false);
    setCurrentScene(0);
    setScores({
      E: 0, I: 0,
      S: 0, N: 0,
      T: 0, F: 0,
      J: 0, P: 0
    });
    setShowFeedback(false);
    setFeedback('');
    setResult(null);
  };

  return (
    <>
      {!isStarted ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4"
        >
         
         <h1 className="text-4xl font-bold text-gray-800 text-center leading-tight mb-2">
         森木
        </h1>
        <h2 className="text-2xl text-gray-600 text-center">沉浸式 MBTI 人格测试</h2>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleStart}
            className="mt-8 px-8 py-4 bg-green-600 text-white text-xl font-semibold rounded-lg shadow-lg hover:bg-green-700 transition-colors"
          >
            开始探索
          </motion.button>
        </motion.div>
      ) : result ? (
        <Result
          type={result}
          description={getMBTIDescription(result)}
          onRestart={handleRestart}
        />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScene}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.75 }}
            className="relative w-full min-h-screen overflow-y-auto bg-white p-4"
          >
            <Scene
              scene={scenes[currentScene]}
              onChoice={handleChoice}
              showFeedback={showFeedback}
              feedback={feedback}
              isTransitioning={isTransitioning}
              onLastSceneComplete={currentScene === scenes.length - 1 ? handleLastSceneComplete : undefined}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}

export default App; 